import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Stack, TextField, Typography } from '@mui/material';

import { getAnswersSchoolFeeding, putAnswers } from 'api/answers/answers.api';
import {
  FormName,
  GetAnswersPayload,
  PostAnswersPayload,
} from 'api/answers/dtos/answers.dto';
import { ListProducts } from 'components/cifa/list_products';
import { ContainerForm } from 'components/shared/container_form';
import { FormButton, FormButtonType } from 'components/shared/form_button';
import { HorizontalStepper } from 'components/shared/horizontal_stepper';
import { Modal } from 'components/shared/modal';
import { PageTitle } from 'components/shared/page_title';
import { SnackbarFeedback } from 'components/shared/snackbar_feedback';
import { ROUTES } from 'routes/constants';
import { NumericFormat } from 'react-number-format';
import ContainerPainel from 'components/container_painel';

export interface Product {
  name: string;
  identification: string;
  value: string;
}

export function formatCnpj(value?: string): string {
  if (!value) return '';
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
}

export function formatCpf(doc: string): string {
  const numbersOnly = doc?.replace(/\D/g, '');
  return numbersOnly?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function addOrUpdateProduct(list: Product[], product: Product) {
  const i = list.findIndex((p) => p.identification === product.identification);
  if (i !== -1) {
    const updated = [...list];
    updated[i] = product;
    return updated;
  }
  return [...list, product];
}

export function removeProductById(list: Product[], id: string) {
  return list.filter((p) => p.identification !== id);
}

export function buildPostPayload(cnpjProducts: Product[], cpfProducts: Product[], provided: string | undefined, applied: string | undefined, userAccessUuid = '', institutionUuid = ''): PostAnswersPayload {
  const valueProvidedByPnae = parseFloat((provided || '').replace(/[^\d.-]/g, '')) || 0;
  const valueAppliedByPnae = parseFloat((applied || '').replace(/[^\d.-]/g, '')) || 0;
  return {
    userAccessUuid,
    institutionUuid,
    formName: FormName.ALIMENTACAO_ESCOLAR_FEDERAL,
    answer: {
      productAquiredFromCnpjByPnaeResourceList: cnpjProducts,
      productAquiredFromCpfByPnaeResourceList: cpfProducts,
      valueProvidedByPnae: valueProvidedByPnae,
      valueAppliedByPnaeResource: valueAppliedByPnae,
    },
    referencedYear: 2025,
  };
}

const EditFederalSchoolFeeding2025: React.FC = () => {
  const navigate = useNavigate();

  const [applied, setApplied] = useState<string>('');
  const [provided, setProvided] = useState<string>('');
  const [value, setValue] = useState<string>('');

  const [name, setName] = useState<string>();
  const [id, setId] = useState<string>();
  const [loading, setLoading] = useState<boolean>();
  const [providedError, setProvidedError] = useState(false);
  const [addModal, setAddModal] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [identification, setidentification] = useState<string>();

  const [cnpjProducts, setCnpjProducts] = useState<Product[]>([]);
  const [cpfProducts, setCpfProducts] = useState<Product[]>([]);

  const handleDeleteCpfProduct = (id: string) => {
    setCpfProducts((prev) => prev.filter((p) => p.identification !== id));
    setidentification(undefined);
    setValue('');
    setName(undefined);
  };

  const handleDeleteCnpjProduct = (id: string) => {
    setCnpjProducts((prev) => prev.filter((p) => p.identification !== id));
    setidentification(undefined);
    setValue('');
    setName(undefined);
  };

  const handleCpfProduct = () => {
    if (name && identification && value) {
      const product: Product = { name, identification, value };
      setCpfProducts((prev) => {
        const i = prev.findIndex((p) => p.identification === identification);
        if (i !== -1) {
          const updated = [...prev];
          updated[i] = product;
          return updated;
        }
        return [...prev, product];
      });
      setidentification(undefined);
      setValue('');
      setName(undefined);
      setAddModal(undefined);
    }
  };

  const handleCnpjProduct = () => {
    if (name && identification && value) {
      const product: Product = { name, identification, value };
      setCnpjProducts((prev) => {
        const i = prev.findIndex((p) => p.identification === identification);
        if (i !== -1) {
          const updated = [...prev];
          updated[i] = product;
          return updated;
        }
        return [...prev, product];
      });
      setidentification(undefined);
      setValue('');
      setName(undefined);
      setAddModal(undefined);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const institutionUuid = localStorage.getItem('institutionUuid') ?? '';
    const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';

    const payload: PostAnswersPayload = {
      userAccessUuid,
      institutionUuid,
      formName: FormName.ALIMENTACAO_ESCOLAR_FEDERAL,
      answer: {
        productAquiredFromCnpjByPnaeResourceList: cnpjProducts,
        productAquiredFromCpfByPnaeResourceList: cpfProducts,
        valueProvidedByPnae: parseFloat(provided.replace(/[^\d.-]/g, '')) || 0,
        valueAppliedByPnaeResource: parseFloat(applied?.replace(/[^\d.-]/g, '')) || 0,
      },
      referencedYear: 2025,
    };
    if (!id) {
      setErrorMessage('Erro: ID do formulário não encontrado.');
      setLoading(false);
      return;
    }

    try {
      await putAnswers(payload, id);
      navigate(ROUTES.EDIT_STATE_SCHOOL_FEEDING_2025);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
        'Erro ao atualizar os dados da Alimentação Escolar (PNAE Federal).',
      );
    } finally {
      setLoading(false);
    }
  };


  const formatCnpj = (value?: string): string => {
    if (!value) return '';
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5',
    );
  };


  const formatCpf = (doc: string): string => {
    const numbersOnly = doc?.replace(/\D/g, '');
    return numbersOnly?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  useEffect(() => {
    const fetchData = async () => {
      const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';
      const institutionUuid = localStorage.getItem('institutionUuid') ?? '';
      const payload: GetAnswersPayload = {
        userAccessUuid,
        institutitonUuid: institutionUuid,
        referencedYear: 2025,
      };

      try {
        const response = await getAnswersSchoolFeeding(payload);
        const form = response?.data.find(
          (form: any) => form.formName === FormName.ALIMENTACAO_ESCOLAR_FEDERAL
        );


        console.log('Form retornado:', form);
        if (form) {
          setId(form?.uuid);
          setProvided(form?.answer?.valueProvidedByPnae);
          setApplied(form?.answer?.valueAppliedByPnaeResource);
          setCnpjProducts(
            form?.answer?.productAquiredFromCnpjByPnaeResourceList,
          );
          setCpfProducts(form?.answer?.productAquiredFromCpfByPnaeResourceList);
        }
      } catch (error) {
        setErrorMessage(
          'Erro ao recuperar os dados da Aba Alimentação Escolar (Federal).',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <ContainerPainel
      pagina='CIFA - Editar Município - 2025'
      showBackButton={true}
      backButtonPath={ROUTES.CIFA2025}
    >
      <HorizontalStepper activeStep={1} />
      <PageTitle
        title='Alimentação Escolar (PNAE Federal)'
        subtitle='Informe corretamente os dados sobre a aquisição de Alimentação Escolar através do PNAE Federal no Município.'
      />
      <Stack marginX='4rem'>
        <ContainerForm title='Valores para Aquisição de Alimentação Escolar - Em 2024'>
          <Stack direction='row' justifyContent='space-between' gap='1rem'>
            <NumericFormat
              id='disponibilizado'
              label='Valor disponibilizado pelo PNAE Federal em R$:'
              value={provided}
              onValueChange={(values) => {
                setProvided(values.value);
                if (values.value) setProvidedError(false);
              }}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              error={providedError}
              helperText={providedError ? 'Este campo é obrigatório.' : ''}
            />
            <NumericFormat
              id='aplicado'
              label='Valores totais aplicados pelo município na Alimentação Escolar oriundos de todo e qualquer fornecedor no ano de 2024 '
              value={applied}
              onValueChange={(values) => {
                setApplied(values.value);
                if (values.value) setProvidedError(false);
              }}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              error={providedError}
              helperText={providedError ? 'Este campo é obrigatório.' : ''}
            />
          </Stack>
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CPF (com recurso do PNAE Federal) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          {cpfProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
            >
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CPF. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}
          {cpfProducts.length > 0 && (
            <ListProducts
              products={cpfProducts}
              documentType={'CPF'}
              edit={(value) => {
                setName(value.name);
                setidentification(value.identification);
                setValue(value.value);
                setAddModal('CPF');
              }}
              remove={(value) => handleDeleteCpfProduct(value.identification)}
            />
          )}
          <FormButton
            type={FormButtonType.PRIMARY}
            title='+ Adicionar Aquisição'
            onClick={() => setAddModal('CPF')}
          />
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CNPJ (com recurso do PNAE Federal) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          {cnpjProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
            >
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CNPJ. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}
          {cnpjProducts.length > 0 && (
            <ListProducts
              products={cnpjProducts}
              documentType={'CNPJ'}
              edit={(value) => {
                setName(value.name);
                setidentification(value.identification);
                setValue(value.value);
                setAddModal('CNPJ');
              }}
              remove={(value) => handleDeleteCnpjProduct(value.identification)}
            />
          )}
          <FormButton
            type={FormButtonType.PRIMARY}
            title='+ Adicionar Aquisição'
            onClick={() => setAddModal('CNPJ')}
          />
        </ContainerForm>

        <Stack
          direction={{ md: 'row-reverse' }}
          marginY='2rem'
          sx={{ gap: '1rem' }}
        >
          <FormButton
            title='Avançar'
            type={FormButtonType.PRIMARY}
            onClick={() => {
              if (!provided || provided === '0') {
                setProvidedError(true);
                return;
              }
              setProvidedError(false);
              handleSubmit();
            }}
            isDisabled={loading}
          />
          <FormButton
            title='Voltar'
            type={FormButtonType.SECONDARY}
            onClick={() => navigate(ROUTES.EDIT_CIFA_2025)}
          />
        </Stack>

        {
          addModal && <Modal
            open={!!addModal}
            title={`Adicionar Aquisição via ${addModal} - Produtos adquiridos com recurso do PNAE Federal`}
            onClose={() => setAddModal(undefined)}
            directionButtons='row'
            buttons={[
              <FormButton
                key={0}
                type={FormButtonType.PRIMARY}
                title='Confirmar'
                onClick={() =>
                  addModal === 'CNPJ' ? handleCnpjProduct() : handleCpfProduct()
                }
              />,
              <FormButton
                key={1}
                type={FormButtonType.SECONDARY}
                title='Voltar'
                onClick={() => {
                  setAddModal(undefined);
                  setName(undefined);
                  setidentification(undefined);
                  setValue('');
                }}
              />,
            ]}
          >
            <>
              <Stack direction='row' justifyContent='space-between' gap='1rem'>
                <TextField
                  id='documento'
                  label={addModal}
                  variant='outlined'
                  value={
                    addModal === 'CPF'
                      ? formatCpf(identification as string)
                      : formatCnpj(identification as string)
                  }
                  onChange={(e) =>
                    setidentification(e.target.value.replace(/\D/g, ''))
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    maxLength: addModal === 'CPF' ? 14 : 18,
                  }}
                  sx={{ flex: 1 }}
                />
                <NumericFormat
                  id='valor'
                  label='Valor'
                  variant='outlined'
                  value={value}
                  onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                  thousandSeparator='.'
                  decimalSeparator=','
                  prefix='R$ '
                  customInput={TextField}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Stack>

              <TextField
                id='nome'
                label='Nome/Razão social'
                variant='outlined'
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ marginTop: '1rem' }}
              />
            </>
          </Modal>
        }


        {errorMessage && (
          <SnackbarFeedback isOpen type='error' message={errorMessage} />
        )}
      </Stack>
    </ContainerPainel>
  );
};

export default EditFederalSchoolFeeding2025;
